import { Formik, FormikConfig, FormikProps } from 'formik';
import { FV, getPlatformsByStores, newScenarioTypes, policysGroupbyPlatforms } from './share';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import useModal from 'hooks/useModal';
import PolicyPreview, { Active } from './PolicyPreview';
import EditModal, { EditModalProps } from 'views/BootGuide/components/EditModal';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import { refreshScenario } from 'views/Multitab/channel';
import ItemHighlightPolicyList, { OnEditUpdate } from './ItemHighlightPolicyList';
import { Badge, Radio } from 'antd';
import RepurchasePeriodDays from './RepurchasePeriodDays';
import styled from '@emotion/styled';
import { useTranslate, T } from '@tolgee/react';
import { useScenarioConfigContext } from '../ScenarioConfigContext';

export interface PolicyFormProps extends FormikConfig<FV> {
  activity: Knowledge.Activity.Detail;
  autoTranslate?: boolean;
  children: (
    policysRender: (
      activeRegion?: General.UpperCaseRegion,
      simplified?: boolean,
      disabled?: boolean,
      onEditUpdate?: OnEditUpdate,
    ) => ReactNode,
    props: FormikProps<FV>,
  ) => ReactNode;
}

function PolicyForm(props: PolicyFormProps) {
  const { activity, autoTranslate, children, onSubmit, ...otherProps } = props;

  const allPlatforms = useMemo(() => getPlatformsByStores(activity.stores), [activity.stores]);

  const validate = useCallback(({ contexts }: FV) => {
    const errors: Partial<Record<keyof FV, any>> = {};

    const hasError = contexts.some(({ regionPolicyList }) => {
      return regionPolicyList.some(({ platformPolicyList }) => {
        return platformPolicyList.some(({ enable, executeContext }) => {
          return enable && !executeContext.content;
        });
      });
    });

    if (hasError) {
      errors.contexts = 'error';
    }

    return errors;
  }, []);

  const { visible, show, hide, meta } = useModal<EditModalProps<string>>();

  const {
    visible: previewVisible,
    show: showPreview,
    hide: hidePreview,
    meta: previewMeta,
  } = useModal<Active>();

  const { refreshScenarioConfigs } = useScenarioConfigContext();

  return (
    <Formik
      enableReinitialize
      validateOnMount
      validate={validate}
      onSubmit={(values, helpers) => {
        onSubmit(values, helpers)?.then(() => {
          values.contexts.forEach(({ scenarioType }) => {
            refreshScenario(scenarioType as any);
          });
          refreshScenarioConfigs();
        });
      }}
      {...otherProps}
    >
      {(formProps) => {
        const {
          values: { contexts },
        } = formProps;

        const editModal = meta && <EditModal visible={visible} onCancel={() => hide()} {...meta} />;

        const previewDrawer = previewMeta && (
          <PolicyPreview
            visible={previewVisible}
            onClose={() => hidePreview()}
            activity={activity}
            allPlatforms={allPlatforms}
            contexts={contexts}
            active={previewMeta}
            onActiveChange={showPreview}
          />
        );

        const policysRender = (
          activeRegion?: General.UpperCaseRegion,
          simplified?: boolean,
          disabled?: boolean,
          onEditUpdate?: OnEditUpdate,
        ) => {
          const { scenarios, newScenarios } = contexts.reduce<{
            scenarios: ReactNode[];
            newScenarios: ReactNode[];
          }>(
            (res, context, index1) => {
              const { activitySendPoint, scenarioType, regionPolicyList } = context;
              regionPolicyList.forEach(({ region, platformPolicyList }, index2) => {
                policysGroupbyPlatforms(platformPolicyList).forEach((policyList) => {
                  if (!policyList.length) {
                    return;
                  }
                  const target = newScenarioTypes.includes(scenarioType)
                    ? res.newScenarios
                    : res.scenarios;
                  target.push(
                    <ItemHighlightPolicyList
                      key={`${activitySendPoint}@${scenarioType}@${region}@${policyList[0].policy.platforms.join(
                        '@',
                      )}`}
                      activity={activity}
                      activeRegion={activeRegion}
                      disabled={disabled}
                      autoTranslate={autoTranslate}
                      simplified={simplified}
                      allPlatforms={allPlatforms}
                      index1={index1}
                      index2={index2}
                      index3s={policyList.map(({ index }) => index)}
                      onEdit={show}
                      onPreview={showPreview}
                      onEditUpdate={onEditUpdate}
                      onCancelEdit={hide}
                    />,
                  );
                });
              });
              return res;
            },
            { scenarios: [], newScenarios: [] },
          );

          return <PolicyRender scenarios={scenarios} newScenarios={newScenarios} />;
        };

        return (
          <PreviewModalProvider>
            {children(policysRender, formProps)}
            {editModal}
            {previewDrawer}
          </PreviewModalProvider>
        );
      }}
    </Formik>
  );
}

function PolicyRender({
  scenarios,
  newScenarios,
}: {
  scenarios: ReactNode[];
  newScenarios: ReactNode[];
}) {
  const { t } = useTranslate();

  const [activeKey, setActiveKey] = useState('scenario');

  if (newScenarios.length === 0) {
    return <>{scenarios}</>;
  }

  return (
    <Container>
      <Radio.Group value={activeKey} onChange={({ target: { value } }) => setActiveKey(value)}>
        <Radio.Button value="scenario">{t('marketing.智能跟单')}</Radio.Button>
        <Radio.Button value="newScenario">
          {t('主动联系买家')}
          <Badge count="NEW" size="small" className="!ml-[4px]" />
        </Radio.Button>
      </Radio.Group>
      <div style={activeKey === 'scenario' ? undefined : { display: 'none' }}>{scenarios}</div>
      <div style={activeKey === 'newScenario' ? undefined : { display: 'none' }}>
        <p>{t('主动联系买家tips')}</p>
        <p>
          <T
            keyName="平均复购周期select"
            params={{
              x: <RepurchasePeriodDays />,
            }}
          />
        </p>
        {newScenarios}
      </div>
    </Container>
  );
}

const Container = styled.div`
  > .ant-radio-group {
    margin-bottom: 16px;
  }
  > div > p > .ant-select {
    margin-left: 12px;
  }
`;

export default PolicyForm;
